/**
 * VaultAidsPanel · curated aids for a dossier with per-aid vault coverage.
 *
 * Displays the top curated aids/grants/tax-credits available for the
 * dossier's country + region + client_type — each with:
 *   - a coverage badge showing how many required docs are already in vault,
 *   - green checks for satisfied documents,
 *   - red flags for still-missing pieces (with a hint about the category).
 *
 * Motivational effect: the more the user uploads, the more aids reach 100%.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  HandCoins, ExternalLink, Loader2, CheckCircle2, XCircle,
  FileText, TrendingUp,
} from "lucide-react";


const CATEGORY_LABEL = {
  grants:       "Subvention",
  gov_programs: "Programme gouv.",
  private_aid:  "Aide",
  tax_credits:  "Crédit d'impôt",
  fiscal:       "Fiscalité",
  financing:    "Financement",
};


export default function VaultAidsPanel({ dossierId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    if (!dossierId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(
        `/global-opportunities/aids-for-dossier/${encodeURIComponent(dossierId)}?limit=10`
      );
      setData(data);
    } catch (err) {
      setData({ items: [], vault_categories_present: [] });
    } finally { setLoading(false); }
  }, [dossierId]);

  useEffect(() => { load(); }, [load]);

  const avgCoverage = useMemo(() => {
    if (!data?.items?.length) return 0;
    return Math.round(
      data.items.reduce((s, a) => s + (a.vault_coverage?.score_pct || 0), 0) /
      data.items.length
    );
  }, [data]);

  if (loading) {
    return (
      <section className="mt-12" data-testid="vault-aids-panel-loading">
        <div className="flex items-center gap-2 text-sm text-[#5B5B62]">
          <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> Chargement des aides…
        </div>
      </section>
    );
  }

  const items = data?.items || [];

  return (
    <section className="mt-14" data-testid="vault-aids-panel">
      <header className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">
            Aides & programmes filtrés selon votre profil
          </div>
          <h2 className="mt-1 font-display text-2xl tracking-tight text-[#0B2545]">
            {items.length} aide{items.length > 1 ? "s" : ""} disponible{items.length > 1 ? "s" : ""}
            <span className="text-[#5B5B62] text-lg font-serif italic ml-2">
              — {avgCoverage}% des documents requis déjà présents
            </span>
          </h2>
          <p className="mt-1 text-xs text-[#5B5B62] max-w-2xl leading-relaxed">
            Chaque aide est croisée avec votre coffre documentaire. Un pourcentage élevé
            signifie qu'il vous reste peu de pièces à fournir pour déposer une demande.
          </p>
        </div>
        {data?.vault_categories_present?.length > 0 && (
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14] max-w-xs text-right">
            Vault : {data.vault_categories_present.join(" · ")}
          </div>
        )}
      </header>

      {items.length === 0 && (
        <div className="rounded-lg border border-dashed border-[#E4E1D8] px-4 py-6 text-xs text-[#5B5B62] text-center"
             data-testid="vault-aids-empty">
          Aucune aide disponible pour ce profil pour l'instant. Complétez le questionnaire
          (pays, région, type de client) pour élargir la recherche.
        </div>
      )}

      <ul className="space-y-3">
        {items.map((aid) => {
          const cov = aid.vault_coverage || { score_pct: 0, satisfied: [], missing: [] };
          const isOpen = expanded[aid.opportunity_id];
          const covTone = cov.score_pct >= 80 ? "emerald"
                          : cov.score_pct >= 50 ? "amber" : "red";
          const covBg = {emerald:"bg-emerald-50 border-emerald-200 text-emerald-800",
                          amber:"bg-amber-50 border-amber-200 text-amber-800",
                          red:"bg-red-50 border-red-200 text-red-800"}[covTone];
          const bMin = aid.benefit?.amount_min || 0;
          const bMax = aid.benefit?.amount_max || 0;
          const amountStr = bMax > 0
            ? `${bMin.toLocaleString("fr-CA")} $ – ${bMax.toLocaleString("fr-CA")} $`
            : "Montant variable";
          return (
            <li key={aid.opportunity_id}
                className="rounded-xl border border-[#E4E1D8] bg-white overflow-hidden"
                data-testid={`vault-aid-${aid.opportunity_id}`}>
              <div className="flex items-start gap-4 px-4 py-3">
                <HandCoins className="h-5 w-5 text-[#7A5B14] flex-shrink-0 mt-0.5" strokeWidth={2} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <h3 className="font-serif text-base text-[#0B2545] leading-tight">
                      {aid.name_fr}
                    </h3>
                    <span className="text-[9px] font-mono uppercase tracking-widest text-[#5B5B62]">
                      {CATEGORY_LABEL[aid.category] || aid.category}
                    </span>
                  </div>
                  <div className="mt-1 text-[11px] text-[#5B5B62]">
                    {aid.provider_fr}
                  </div>
                  <div className="mt-1.5 text-xs text-[#1F2937] leading-snug">
                    {aid.summary_fr}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] font-mono">
                    <span className="text-[#7A5B14]">💰 {amountStr}</span>
                    {aid.process?.typical_delay_weeks && (
                      <span className="text-[#5B5B62]">
                        ⏱ ~{aid.process.typical_delay_weeks} sem.
                      </span>
                    )}
                    {aid.process?.success_rate_pct != null && (
                      <span className="text-[#5B5B62]">
                        📈 {aid.process.success_rate_pct}% de succès
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex-shrink-0 text-right">
                  <div className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-mono ${covBg}`}
                       data-testid={`vault-aid-coverage-${aid.opportunity_id}`}>
                    <TrendingUp className="h-3 w-3" strokeWidth={2} />
                    {cov.score_pct}%
                  </div>
                  <div className="mt-1 text-[9px] font-mono text-[#8B8B8F]">
                    {cov.satisfied.length}/{cov.total || 0} pièce{cov.total > 1 ? "s" : ""}
                  </div>
                </div>
              </div>

              {/* Expandable requirements */}
              {(cov.satisfied.length > 0 || cov.missing.length > 0) && (
                <div className="border-t border-[#EDE7D6] bg-[#FBF7EE]/40 px-4 py-2.5">
                  <button
                    onClick={() => setExpanded((p) => ({ ...p, [aid.opportunity_id]: !p[aid.opportunity_id] }))}
                    data-testid={`vault-aid-toggle-${aid.opportunity_id}`}
                    className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14] hover:text-[#0B2545]"
                  >
                    {isOpen ? "— Masquer les documents requis" : "+ Voir les documents requis"}
                  </button>
                  {isOpen && (
                    <ul className="mt-2 space-y-1">
                      {cov.satisfied.map((s, i) => (
                        <li key={`s-${i}`} className="flex items-center gap-2 text-[11px] text-emerald-800">
                          <CheckCircle2 className="h-3 w-3 flex-shrink-0" strokeWidth={2} />
                          <span>{s.label_fr}</span>
                          <span className="text-[9px] font-mono text-emerald-700/60">
                            ✓ dans vault ({s.vault_category})
                          </span>
                        </li>
                      ))}
                      {cov.missing.map((m, i) => (
                        <li key={`m-${i}`} className="flex items-center gap-2 text-[11px] text-red-800">
                          <XCircle className="h-3 w-3 flex-shrink-0" strokeWidth={2} />
                          <span>{m.label_fr}</span>
                          {m.vault_category && (
                            <span className="text-[9px] font-mono text-red-700/60">
                              catégorie « {m.vault_category} »
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-2 flex items-center gap-2">
                    {aid.source_url && (
                      <a href={aid.source_url} target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#0B2545] hover:text-[#7A5B14]"
                         data-testid={`vault-aid-source-${aid.opportunity_id}`}>
                        <ExternalLink className="h-3 w-3" strokeWidth={2} /> Source officielle
                      </a>
                    )}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
